import type { SDKOptions } from "~/types/options";
import type { Schools, POIs } from "@local-logic/client";
import { TravelMode } from "./hooks/useCommute/types";

export type MapProviderNames = "maptiler" | "mapbox" | "google";
export type MapProvider = {
  name: MapProviderNames;
  key?: string;
  theme?: string;
};
export type CommuteOptions = {
  destination: { lat: number; lng: number } | undefined;
  commuteType: TravelMode | undefined;
  label?: string;
};

export type POISubcategory = keyof POIs | keyof Schools;

export type CategoryKey =
  | "transport"
  | "education"
  | "amenities"
  | "character"
  | "nature"
  | "wellness";

export type TransportScoreKey =
  | "pedestrian_friendly"
  | "transit_friendly"
  | "cycling_friendly"
  | "car_friendly";
export type EducationScoreKey = "daycares" | "primary_schools" | "high_schools";
export type AmenitiesScoreKey =
  | "shopping"
  | "groceries"
  | "restaurants"
  | "cafes";
export type CharacterScoreKey = "nightlife" | "vibrant" | "historic" | "quiet";
export type NatureScoreKey = "parks" | "greenery";
export type WellnessScoreKey = "wellness";
export type ScoreKey =
  | TransportScoreKey
  | EducationScoreKey
  | AmenitiesScoreKey
  | CharacterScoreKey
  | NatureScoreKey
  | WellnessScoreKey;

export type ScoreOptions = {
  hide?: boolean;
  hideScore?: boolean;
  order?: number;
};

export type CategoryOptions = {
  hide?: boolean;
  order?: number;
  colors?: {
    background: string;
    fill: string;
  };
  scores?: {
    [key in ScoreKey]?: ScoreOptions;
  };
};

export type Options = {
  lat?: number;
  lng?: number;
  zoom?: number;
  pitch?: number;
  bearing?: number;
  distanceUnit?: "metric" | "imperial";
  marker?: {
    lat: number;
    lng: number;
    icon?: "house" | "pin";
    // Used to show the address over the marker, and for the LC LAT CTA
    addressLabel?: string;
  };
  cooperativeGestures?: boolean;
  mapProvider?: MapProvider;
  hideScores?: boolean;
  collapseScores?: boolean; // TODO Might not need this
  scoresMenu?: {
    defaultOpen?: boolean;
    title?: boolean | string;
    categories?: {
      [key in CategoryKey]?: CategoryOptions;
    };
  };
};

export type LocalContentProps = SDKOptions<
  Options,
  {
    options: Options | undefined;
  }
>;

// TODO revisit events
export enum TrackingPipelineEvents {
  SDK_VIEWED = "LC-Viewed",
  SIDEBAR_TOGGLE_CLICKED = "LC-Sidebar-Toggle-Clicked",
  SIDEBAR_CLICKED_WHILE_CLOSED = "LC-Sidebar-Clicked-While-Closed",
  SIDEBAR_SCORE_SELECTED = "LC-Sidebar-Score-Selected",
  SIDEBAR_POI_SELECTED = "LC-Sidebar-POI-Selected",

  CATEGORIES_MENU_TOGGLE_CLICKED = "LC-Categories-Menu-Toggle-Clicked",
  CATEGORIES_MENU_AUTO_CLOSED = "LC-Categories-Menu-Auto-Closed",
  CATEGORIES_MENU_SCORE_TOGGLED = "LC-Categories-Menu-Score-Toggled",
  CATEGORIES_MENU_CATEGORY_SELECTED = "LC-Categories-Menu-Category-Selected",

  SCORE_POI_CATEGORY_SELECTED = "LC-Score-POI-Category-Selected",
  MAP_POI_SELECTED = "LC-Map-POI-Selected",
  MAP_POI_SELECTED_FROM_CLUSTER = "LC-Map-POI-Selected-From-Cluster",
  MAP_POI_POPUP_LINK_CLICKED = "LC-Map-POI-Popup-Link-Clicked",
  MAP_POI_VIEW_ROUTE = "LC-Map-POI-View-Route",

  COMMUTE_CALCULATOR_CALCULATE_ROUTE = "LC-Commute-Calculator-Calculate-Route",

  CATEGORY_CLICKED = "LC-Category-Clicked",
  SCORE_CLICKED = "LC-Score-Clicked",
  TRANSPORT_METHOD_CLICKED = "LC-Transportation-Method-Clicked",
  LONG_DESCRIPTION_CLICKED = "LC-Long-Description-Clicked",
  POI_CLICKED = "LC-POI-Clicked",
  COMMUTE_CALCULATOR_CLICKED = "LC-Commute-Comute-Calc-Clicked",
  COMMUTE_CALCULATE_ROUTE = "LC-Commute-Calculate-Route",
  ATTRIBUTION_CLICKED = "LC-Attribution-Clicked",
}

// Why are these named differently?
// https://docs.mixpanel.com/docs/data-structure/events-and-properties#name-events-and-properties-consistently
export enum AnalyticsEvents {
  SDK_VIEWED = "sdk_viewed",
  SIDEBAR_TOGGLE_CLICKED = "sidebar_toggle_clicked",
  SIDEBAR_CLICKED_WHILE_CLOSED = "sidebar_clicked_while_closed",
  SIDEBAR_SCORE_SELECTED = "sidebar_score_selected",
  SIDEBAR_POI_SELECTED = "sidebar_poi_selected",

  CATEGORIES_MENU_TOGGLE_CLICKED = "categories_menu_toggle_clicked",
  CATEGORIES_MENU_AUTO_CLOSED = "categories_menu_auto_closed",
  CATEGORIES_MENU_SCORE_TOGGLED = "categories_menu_score_toggled",
  CATEGORIES_MENU_CATEGORY_SELECTED = "categories_menu_category_selected",

  SCORE_POI_CATEGORY_SELECTED = "score_poi_category_selected",
  MAP_POI_SELECTED = "map_poi_selected",
  MAP_POI_SELECTED_FROM_CLUSTER = "map_poi_selected_from_cluster",
  MAP_POI_POPUP_LINK_CLICKED = "map_poi_popup_link_clicked",
  MAP_POI_VIEW_ROUTE = "map_poi_view_route",

  COMMUTE_CALCULATOR_CALCULATE_ROUTE = "commute_calculator_calculate_route",

  CATEGORY_SELECTED = "category_selected",
  SCORE_EXPANDED = "score_expanded",
  SCORE_POI_SELECTED = "score_poi_selected",
  POI_CALCULATE_ROUTE = "poi_calculate_route",

  SEARCH_CALCULATE_ROUTE = "search_calculate_route",
}
